import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider } from 'react-redux';

import {
  TeacherApplicationContext,
  TeacherApplicationContextProvider,
} from '~shared/context/TeacherApplicationContext';
import { RouterProvider, TeacherRouter } from '~teacher/context/RouterContext';
import {
  ActivityLivePage,
  ActivityPage,
  ActivityQuestionPage,
} from '~teacher/pages';
import { queryClient } from '~teacher/queryClient';

import header from '../../../teacher/header/HeaderController';
import store from '../../store';

interface DoActivityResultParams {
  activityInstanceId: number;
  socrativeRouter: TeacherRouter;
  teacherApplicationContext: TeacherApplicationContext;
  showInsights?: boolean;
}

function doActivityResult({
  activityInstanceId,
  socrativeRouter,
  teacherApplicationContext,
  showInsights = false,
}: DoActivityResultParams) {
  header.render('reports');

  ReactDOM.render(
    <TeacherApplicationContextProvider
      applicationContext={teacherApplicationContext}
    >
      <RouterProvider router={socrativeRouter}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <Provider store={store}>
            <div className="font-sans results-container">
              <ActivityPage
                activityInstanceId={activityInstanceId}
                showInsights={showInsights}
              />
            </div>
          </Provider>
        </QueryClientProvider>
      </RouterProvider>
    </TeacherApplicationContextProvider>,
    document.getElementById('main-content')
  );
}

interface DoActivityResultQuestionParams {
  activityInstanceId: number;
  questionPosition: number;
  socrativeRouter: TeacherRouter;
  teacherApplicationContext: TeacherApplicationContext;
}

function doActivityResultQuestion({
  activityInstanceId,
  questionPosition,
  socrativeRouter,
  teacherApplicationContext,
}: DoActivityResultQuestionParams) {
  ReactDOM.render(
    <TeacherApplicationContextProvider
      applicationContext={teacherApplicationContext}
    >
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <Provider store={store}>
          <RouterProvider router={socrativeRouter}>
            <div
              className="font-sans results-container"
              id="live-results-main-content"
            >
              <ActivityQuestionPage
                activityInstanceId={activityInstanceId}
                questionPosition={questionPosition}
              />
            </div>
          </RouterProvider>
        </Provider>
      </QueryClientProvider>
    </TeacherApplicationContextProvider>,
    document.getElementById('main-content')
  );
}

function doLiveActivityResult(router: TeacherRouter) {
  // Re-render the header so the StudentMenu mounts on the live-results route and
  // picks up the current roster + hand-raise. Without this the header keeps the
  // roster captured at room-join, so students who joined after launch (and their
  // hand-raise state) never appear for Quick Question / Exit Ticket / Quiz.
  // Mirrors doActivityResult('reports') and SpaceRaceController's render('results').
  header.render('results');
  ReactDOM.render(
    <RouterProvider router={router}>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <Provider store={store}>
          <ActivityLivePage />
        </Provider>
      </QueryClientProvider>
    </RouterProvider>,
    document.getElementById('main-content')
  );
}

export default {
  doActivityResult,
  doActivityResultQuestion,
  doLiveActivityResult,
};
